import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  batchRemoveFilesApiV1FilesBatchDeletePost,
  retrySummaryGenerationApiV1FilesIdRetrySummaryGenerationPost,
} from "../../api/generated/sdk.gen";
import type { DocSorterFileSchema } from "../../api/generated/types.gen";
import { MUTATION_KEYS } from "../../constants/clientKeys";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { buildApiErrorMessage } from "../../utils/notification";

/**
 * Hook for bulk operations on files
 */
export const useBulkFileOperations = () => {
  const queryClient = useQueryClient();

  const bulkRemove = useMutation({
    mutationKey: MUTATION_KEYS.BULK_REMOVE_FILES,
    mutationFn: async (fileIds: string[]) => {
      const response = await batchRemoveFilesApiV1FilesBatchDeletePost({
        body: { file_ids: fileIds },
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to batch delete files", response.error)
        );
      }

      const data = response.data!;
      const deletedSet = new Set(data.deleted_file_ids);

      const results = fileIds.map((fileId) => ({
        fileId,
        success: deletedSet.has(fileId),
        ...(deletedSet.has(fileId)
          ? {}
          : { error: "File not found or not owned by user" }),
      }));

      const errors = data.not_found_file_ids.map((fileId) => ({
        fileId,
        error: "File not found or not owned by user",
      }));

      return {
        results,
        totalProcessed: fileIds.length,
        successCount: data.deleted_count,
        errorCount: errors.length,
        errors,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
    },
    onError: (error) => {
      console.error("Error in bulk remove operation:", error);
    },
  });

  const bulkRetrySummary = useMutation({
    mutationFn: async (fileIds: string[]) => {
      const results: Array<{
        fileId: string;
        success: boolean;
        data?: unknown;
        error?: string;
      }> = [];
      const errors: Array<{ fileId: string; error: string }> = [];

      // Process files in parallel but with a concurrency limit
      const BATCH_SIZE = 3; // Process 3 files at a time for summary generation
      for (let i = 0; i < fileIds.length; i += BATCH_SIZE) {
        const batch = fileIds.slice(i, i + BATCH_SIZE);

        const batchPromises = batch.map(async (fileId) => {
          try {
            const response =
              await retrySummaryGenerationApiV1FilesIdRetrySummaryGenerationPost({
                path: { id: fileId },
                client: apiClient,
              });

            if ("error" in response && response.error) {
              throw new Error(
                buildApiErrorMessage(
                  `Failed to retry summary for file ${fileId}`,
                  response.error
                )
              );
            }

            return { fileId, success: true, data: response.data };
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : String(error);
            return { fileId, success: false, error: errorMessage };
          }
        });

        const batchResults = await Promise.allSettled(batchPromises);

        batchResults.forEach((result) => {
          if (result.status === "fulfilled") {
            results.push(result.value);
            if (!result.value.success) {
              errors.push({
                fileId: result.value.fileId,
                error: result.value.error || "Unknown error",
              });
            }
          } else {
            // This shouldn't happen with our current setup, but just in case
            const fileId = batch[batchResults.indexOf(result)];
            const errorMessage =
              result.reason instanceof Error
                ? result.reason.message
                : String(result.reason);
            errors.push({ fileId, error: errorMessage });
            results.push({ fileId, success: false, error: errorMessage });
          }
        });
      }

      return {
        results,
        totalProcessed: fileIds.length,
        successCount: results.filter((r) => r.success).length,
        errorCount: errors.length,
        errors,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.FILES],
      });
    },
    onError: (error) => {
      console.error("Error in bulk retry summary operation:", error);
    },
  });

  return {
    bulkRemove,
    bulkRetrySummary,
  };
};

/**
 * Hook for file filtering and management utilities
 */
export const useFileUtils = () => {
  /**
   * Filter files by status
   */
  const filterByStatus = (
    files: DocSorterFileSchema[],
    status: string | string[]
  ) => {
    const statusArray = Array.isArray(status) ? status : [status];
    return files.filter((file) => file.status && statusArray.includes(file.status));
  };

  /**
   * Filter files by classification
   */
  const filterByClassification = (
    files: DocSorterFileSchema[],
    classification: string | string[]
  ) => {
    const classificationArray = Array.isArray(classification)
      ? classification
      : [classification];
    return files.filter(
      (file) =>
        file.classification && classificationArray.includes(file.classification)
    );
  };

  /**
   * Group files by classification
   */
  const groupByClassification = (files: DocSorterFileSchema[]) => {
    return files.reduce((groups: Record<string, DocSorterFileSchema[]>, file) => {
      const classification = file.classification || "Uncategorized";
      if (!groups[classification]) {
        groups[classification] = [];
      }
      groups[classification].push(file);
      return groups;
    }, {});
  };

  /**
   * Group files by status
   */
  const groupByStatus = (files: DocSorterFileSchema[]) => {
    return files.reduce((groups: Record<string, DocSorterFileSchema[]>, file) => {
      const status = file.status || "TO_BE_PROCESSED";
      if (!groups[status]) {
        groups[status] = [];
      }
      groups[status].push(file);
      return groups;
    }, {});
  };

  /**
   * Sort files by creation date
   */
  const sortByCreatedAt = (
    files: DocSorterFileSchema[],
    order: "asc" | "desc" = "desc"
  ) => {
    return [...files].sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    });
  };

  /**
   * Sort files by updated date
   */
  const sortByUpdatedAt = (
    files: DocSorterFileSchema[],
    order: "asc" | "desc" = "desc"
  ) => {
    return [...files].sort((a, b) => {
      const dateA = new Date(a.updated_at || 0).getTime();
      const dateB = new Date(b.updated_at || 0).getTime();
      return order === "asc" ? dateA - dateB : dateB - dateA;
    });
  };

  /**
   * Get files with failed status
   */
  const getFailedFiles = (files: DocSorterFileSchema[]) => {
    return filterByStatus(files, "FAILED");
  };

  /**
   * Get files with processing status
   */
  const getProcessingFiles = (files: DocSorterFileSchema[]) => {
    return filterByStatus(files, ["TO_BE_PROCESSED", "PROCESSING"]);
  };

  /**
   * Get completed files
   */
  const getCompletedFiles = (files: DocSorterFileSchema[]) => {
    return filterByStatus(files, "COMPLETED");
  };

  /**
   * Calculate file statistics
   */
  const getFileStatistics = (files: DocSorterFileSchema[]) => {
    const stats = {
      total: files.length,
      byStatus: {} as Record<string, number>,
      byClassification: {} as Record<string, number>,
    };

    files.forEach((file) => {
      // Count by status
      const status = file.status || "TO_BE_PROCESSED";
      stats.byStatus[status] = (stats.byStatus[status] || 0) + 1;

      // Count by classification
      const classification = file.classification || "Uncategorized";
      stats.byClassification[classification] =
        (stats.byClassification[classification] || 0) + 1;
    });

    return stats;
  };

  return {
    filterByStatus,
    filterByClassification,
    groupByClassification,
    groupByStatus,
    sortByCreatedAt,
    sortByUpdatedAt,
    getFailedFiles,
    getProcessingFiles,
    getCompletedFiles,
    getFileStatistics,
  };
};
